import Insight from "../insightClass";
import React from "react";
import { insightCBAMMedia } from "../../../assets/assetReference";
import { ContactSubscribe } from "../../../components/Contact/Contact";
export const insight097 = new Insight(
  "13 February 2026",
  {
    pageTitle:
      "CBAM: a complete guide to data collection for offshore wind manufacture",
    metaDescription:
      "WeBridge Insights, CBAM: a complete guide to data collection for offshore wind manufacture",
    keyWords:
      "European Offshore Wind Market development, CBAM: a complete guide to data collection for offshore wind manufacture",
  },
  {
    src: insightCBAMMedia[4],
    alt: "CBAM",
  },
  "CBAM: A Complete Guide To Data Collection for Offshore Wind Manufacture",
  "Both non-EU manufacturers of offshore wind components and their EU customers face significant data collection obligations under CBAM. But if they get it right, the commercial benefits will be nothing short of transformative.",
  `To comply with CBAM and avoid costly project delays, clear cooperation and communication between European offshore wind developers and their suppliers is now mission critical. Our expertise in CBAM and the global offshore wind supply chain can help to make sure your offshore wind projects run smoothly and profitably. `,
  <div>
    <strong>WeBridge Insights</strong>
    <h1>
      CBAM: A Complete Guide To Data Collection for Offshore Wind Manufacture
    </h1>
    <h2>
      Both non-EU manufacturers of offshore wind components and their EU
      customers face significant data collection obligations under CBAM. But if
      they get it right, the commercial benefits will be nothing short of
      transformative.
    </h2>
    <p className="article-date">13 February 2026</p>
    <br />

    <h3>Before we begin…</h3>
    <p>
      To comply with CBAM and avoid costly project delays, clear cooperation and
      communication between European offshore wind developers and their
      suppliers is now mission critical. Our expertise in CBAM and the global
      offshore wind supply chain can help to make sure your offshore wind
      projects run smoothly and profitably.{" "}
      <a
        href="https://www.windenergybridge.com/about-us"
        target="_blank"
        rel="noopener noreferrer"
        style={{ wordBreak: "break-all" }}
      >
        Here's what you can expect from us
      </a>
      .
    </p>
    <h3>Introduction</h3>
    <p>
      The Carbon Border Adjustment Mechanism (CBAM) is the European Union's
      landmark policy to put a carbon price on imported goods based on their
      production emissions<sup>[1]</sup>. From 1<sup>st</sup> January 2026
      importers in the EU have been required to pay a carbon tariff on products
      like steel and aluminium according to the CO<sub>2</sub> emitted during
      manufacturing<sup>[1]</sup>. This first-of-its-kind measure aims to
      prevent "carbon leakage" - ensuring that domestic industries that face
      carbon costs under EU climate rules are not undercut by cheaper,
      high-emission imports<sup>[2]</sup>. In essence, CBAM levels the playing
      field: if foreign manufacturers produce goods with high greenhouse gas
      emissions, those imports will incur a fee at the EU border - encouraging
      cleaner production globally<sup>[3]</sup>.
    </p>
    <p>
      Accurate data collection is at the heart of CBAM. During the transitional
      phase (2023-2025), EU importers were required to report the embedded
      emissions of covered imports quarterly<sup>[4]</sup>, obtaining the
      necessary data from their supply chains<sup>[5]</sup>. From 2026 onward,
      these emissions data translate into financial obligations. Importers must
      purchase and surrender CBAM certificates equivalent to the reported
      emissions<sup>[6]</sup>
      <sup>[7]</sup>.{" "}
    </p>
    <p>
      This makes reliable monitoring and reporting of emissions absolutely
      critical: companies need to quantify both direct emissions (from on-site
      fuel use or industrial processes) and indirect emissions (from electricity
      use) for each imported product. High-quality emissions data allows
      importers to accurately determine how many certificates to buy and ensures
      they only pay for the actual carbon footprint of their goods, rather than
      a potentially inflated 'default' value.{" "}
    </p>
    <p>
      Offshore wind development, a rapidly growing industry across the EU,
      illustrates the stakes and implications of CBAM. Building offshore wind
      turbines requires massive steel components - notably monopile and jacket
      foundations and turbine towers - which are emissions-intensive to produce.
      Steel and iron products are among the first goods covered by CBAM
      <sup>[6]</sup>. Indeed steel structures like wind turbine foundations fall
      squarely within CBAM's scope<sup>[8]</sup>.{" "}
    </p>
    <p>
      If these foundations or their materials are sourced from outside the EU,
      the importer (such as a European offshore wind developer or contractor)
      must report their embedded CO<sub>2</sub> emissions and will eventually
      pay the corresponding carbon costs. This has direct implications for
      offshore wind fabrication: manufacturers and suppliers of wind turbine
      foundations need robust emissions monitoring in place to remain
      competitive in the EU market.{" "}
    </p>
    <h3>Why data collection matters</h3>
    <p>
      CBAM's effectiveness and fairness hinges on accurate emissions data. The
      system distinguishes between 'actual' emissions and 'default' values.
      Actual emissions are the real, measured greenhouse gases emitted in
      producing a specific shipment of goods. CBAM encourages using actual data
      whenever possible, since this reflects a producer's true carbon footprint.
      However, calculating actual emissions requires robust data collection and
      monitoring at the factory level - often a complex task when supply chains
      involve multiple inputs and processes.{" "}
    </p>
    <p>
      If actual data cannot be obtained or verified, default values serve as a
      fallback. CBAM's default values are set to be conservative (i.e. somewhat
      punitive) to encourage real data reporting: they are based on the average
      emission intensity of the exporting country for that product, plus a
      markup that increases over the first few years of implementation. For
      example, in 2026 the default emissions value includes a 10% markup (rising
      to 30% by 2028) over the country-average intensity.{" "}
    </p>
    <p>
      This means if exporters do not provide data and instead rely on defaults,
      their goods could appear "dirtier" than they actually are - and that means
      higher costs. Thus, both EU importers and foreign exporters have a shared
      interest in reliable data collection. Importers need the data to minimise
      how many certificates they must buy, and exporters need it to keep their
      products competitive.{" "}
    </p>
    <p>The implications for offshore wind</p>
    <p>
      The offshore wind sector is an example of an industry that, while driving
      the clean energy transition, relies on emissions-intensive inputs. Wind
      turbines require large amounts of steel - especially in the foundations
      and towers. Under CBAM, the embedded carbon in those steel components
      becomes an economic factor.{" "}
    </p>
    <p>
      Europe's push for wind expansion has made it a significant consumer of
      heavy steel plate. In fact, roughly 15-20% of Europe's demand for heavy
      steel plate is now driven by wind power projects, and this share is
      expected to exceed 25% within a few years<sup>[9]</sup>. Yet while onshore
      projects are more numerous<sup>[10]</sup>, it's offshore wind projects
      that dominate demand for heavy steel due to the increased size, scale and
      engineering challenges associated with offshore developments. Indeed it's
      estimated that while onshore turbines require 50 tonnes of steel for every
      MW of power output, offshore turbines might require as much as 300 tonnes
      per MW<sup>[11]</sup>.
    </p>
    <p>
      If domestic steel supply cannot meet this booming demand, project
      developers may import steel or finished components. For instance, Chinese
      manufacturers have already supplied components (including foundations and
      towers, etc) for a number of offshore wind projects in several European
      countries including UK, France, Germany and Poland<sup>[12]</sup>.{" "}
    </p>
    <p>
      &gt;&gt;{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ wordBreak: "break-all" }}
        href="https://www.windenergybridge.com/insights/keeping-offshore-wind-developments-on-track-in-a-time-of-expensive-capital"
      >
        Keeping offshore wind developments on track in a time of expensive
        capital
      </a>
    </p>
    <p>
      &gt;&gt;{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ wordBreak: "break-all" }}
        href="https://www.windenergybridge.com/insights/6-reasons-why-uk-offshore-wind-requires-a-'glocal'-approach"
      >
        6 reasons why UK offshore wind requires a 'glocal' approach
      </a>
    </p>
    <p>
      Only with accurate data - e.g. measured CO<sub>2</sub> per tonne of steel
      - can the carbon tariff for such imports be correctly assessed. This
      creates a strong incentive for manufacturers of offshore wind equipment to
      document and reduce emissions in their production. For example, if a
      monopile manufacturer in East Asia wants to supply the EU market, it will
      need to measure its factory's fuel and electricity use, and obtain
      emissions data from its own steel suppliers, so that an EU buyer can
      accurately report the monopile's carbon footprint.{" "}
    </p>
    <p>
      There's an element of commercial competitiveness to this too. If a
      European offshore wind developer is comparing bids, a supplier that can
      demonstrate lower embedded emissions (e.g. by using cleaner steel or
      renewable energy in fabrication) will effectively incur a lower carbon
      tariff under CBAM, potentially making their offer more attractive. In this
      way, CBAM's data requirements reinforce trends toward greener steel and
      manufacturing in the wind industry, aligning with broader climate goals.
    </p>
    <h3>CBAM data collection for non-EU offshore wind manufacturers</h3>
    <p>
      Under CBAM, offshore wind manufacturers and exporters outside the EU are
      responsible for monitoring and reporting the emissions of the goods they
      produce and send to Europe. The European Commission's guidance for such
      operators lays out methodologies to ensure consistent and verifiable data
      collection. In practical terms, a steel fabrication plant - for instance,
      a factory producing offshore wind foundations in Asia - must put in place
      a Monitoring Methodology Document (MMD). This document acts as a blueprint
      for how the company measures all relevant emissions and production data,
      so that the resulting carbon footprint calculations are reliable and
      standardised.
    </p>
    <h4 className="font-bold">Defining boundaries and processes</h4>
    <p>
      An operator must first define the system boundaries of their
      installation's emissions monitoring. This means clarifying the processes
      and sources of emissions that are included. For example, consider a plant
      that manufactures monopile foundations for wind turbines. The boundaries
      would encompass all on-site activities that generate emissions: running
      the plate-rolling machines, welding operations, any heating or cutting
      processes, on-site generators or boilers etc. The guidance suggests
      splitting the installation into specific production processes
      corresponding to the CBAM "aggregated goods" categories - such as steel,
      iron, aluminium and so on.{" "}
    </p>
    <p>
      For many offshore wind foundation manufacturers, the primary product
      category will be iron and steel structures (including items such as large
      tubes, plates and assembled structures under CN code 7308<sup>[8]</sup>).
      If the facility produces multiple types of goods in the same category, it
      may use a "bubble" approach - treating them as a joint process - but this
      typically applies more to integrated steelworks that produce varied steel
      products. A monopile yard likely has one main product category (steel
      structures) and would monitor that as a whole.
    </p>
    <h4 className="font-bold">Direct vs indirect emissions</h4>
    <p>
      Data collection must distinguish between direct emissions and indirect
      emissions from the installation.
    </p>
    <h5 className="text-lg font-bold">Direct emissions</h5>
    <p>
      Direct emissions are those emitted on-site from sources that the operator
      controls. According to the CBAM guidelines, this includes emissions from
      fuel combustion (e.g. burning natural gas, diesel or other fuels in
      furnaces, generators, or vehicles), process emissions from chemical
      reactions (e.g. CO<sub>2</sub> released during welding or cutting
      chemistry or from concrete curing etc.), and emissions from producing
      measurable heat on-site.{" "}
    </p>
    <p>
      In a foundation fabrication yard, direct emissions could come from things
      like gas-fired ovens used to pre-heat steel plates for bending, diesel
      generators or forklifts operating on the premises, or any on-site
      combustion processes during surface treatment. All such fuel and process
      usage needs to be measured. The activity data for direct emissions -
      meaning the quantities of fuels consumed or materials processed - should
      be determined either by continuous metering (installing flow meters, fuel
      gauges etc. for real-time tracking) or by batch measurements summed over
      the year, accounting for stock changes.{" "}
    </p>
    <p>
      For example, a monopile factory might meter its natural gas usage in a
      heating furnace throughout the year and track how many litres of diesel
      were used by all its equipment to quantify fuel consumption precisely. The
      operator must also assign appropriate emission factors to each fuel (the
      amount of CO<sub>2</sub> emitted per unit of fuel burned) and other
      calculation factors like oxidation rates. These can be taken from standard
      reference values (e.g. national greenhouse gas inventory data or default
      factors provided in EU regulations) or determined via laboratory analysis
      for higher accuracy. In short, the plant needs a system to record how much
      fuel it uses before multiplying that by known emission rates to calculate
      direct CO<sub>2</sub> output.
    </p>
    <h5 className="text-lg font-bold">Indirect emissions</h5>
    <p>
      Indirect emissions refer to the emissions from the generation of
      electricity that the installation consumes. Many industrial facilities use
      significant electricity for machinery, lighting, ventilation, welding etc.
      In our example, welding machines shaping thick steel plates into a
      monopile segment draw a lot of power. These emissions don't come out of
      the factory's own stack, but from the power plants supplying the
      grid.{" "}
    </p>
    <p>
      Under CBAM, operators must monitor how much electricity (in MWh) they
      consume and apply an emission factor for the electricity. The factor
      depends on the source: if the electricity is taken from the public grid, a
      default grid emission factor (provided by the European Commission based on
      international energy agency data) is used. If the operator has its own
      power production (for example, an on-site diesel generator or a renewable
      power source) or a direct power purchase agreement with a specific energy
      supplier, then an actual emission factor reflecting that generation can be
      used.{" "}
    </p>
    <p>
      It's worth noting a special rule: if an operator produces electricity
      on-site (say, running a captive power plant), the fuel emissions from that
      production count as the installation's direct emissions, but they are not
      attributed to the goods' direct emissions - instead, the electricity is
      treated as a separate process with its own indirect emission factor for
      the product. In simpler terms, if our monopile factory runs a diesel
      generator for power, the diesel combustion is logged as a direct emission
      source, but when calculating the monopile's footprint, that emission is
      accounted via the electricity's indirect emissions factor rather than
      double counting it as a direct emission of the product.{" "}
    </p>
    <p>
      For the data collector, the key task is to accurately log electricity
      usage (via electricity meters) and know the origin of that electricity to
      apply the correct CO<sub>2</sub> per kWh value.
    </p>
    <h4 className="font-bold">Precursors and embedded emissions</h4>
    <p>
      A critical part of CBAM data collection is dealing with precursors - input
      materials that were produced elsewhere, which themselves have embedded
      emissions. For a manufacturer of offshore wind foundations, the most
      significant precursor is typically steel plate (or steel sections)
      purchased from a steel mill.{" "}
    </p>
    <p>
      CBAM rules require operators to obtain specific emissions data from the
      suppliers of such precursors. In practice, if a monopile factory buys
      rolled steel plates from a steel mill (which could even be in another
      country), the factory should request the mill to provide the specific
      embedded emissions of those plates (in tonnes CO<sub>2</sub> per tonne of
      steel).{" "}
    </p>
    <p>
      According to the guidance, for each precursor material the operator uses,
      they should collect:
    </p>
    <ul className="list-disc list-inside">
      <li>
        <b>The identification of the installation</b> where that precursor was
        produced (e.g. which steel mill made the plate).
      </li>
      <li>
        <b>The direct and indirect emissions</b> per tonne of the precursor (for
        instance, the steel mill's own reported CO<sub>2</sub> per tonne of
        steel, covering its fuel use and electricity use).
      </li>
      <li>
        <b>Details on the production route</b> of the precursor and any key
        parameters (e.g. whether the steel was made via an electric arc furnace
        with scrap or a blast furnace route, what the alloy content is and so
        on).
      </li>
      <li>
        <b>The reporting period</b> the data covers (to ensure alignment - this
        is usually the calendar year).
      </li>
      <li>
        <b>Information on any carbon price paid</b> for that precursor in its
        country of origin (if the steel mill paid a carbon tax or had emissions
        trading costs, and whether they got any rebates or free allocations).
      </li>
    </ul>
    <br />
    <p>
      The offshore foundation manufacturer must gather this level of detail for
      each batch of steel or other significant material they use. If the
      supplier cannot or will not provide the data, the CBAM regulation does
      allow using default values for precursors - but only under strict
      conditions. Specifically, default emissions values (likely higher than
      actual) can be used provided the precursors account for less than 20% of
      the total emissions embedded in the final good.{" "}
    </p>
    <p>
      In many cases for steel-heavy products, the steel accounts for the
      majority of the emissions. For example, producing 1 tonne of crude steel
      via a traditional blast furnace route emits roughly 1.8-2.2 tonnes of CO
      <sub>2</sub> on average<sup>[13]</sup>. If that tonne of steel is made
      into a structure, the fabrication processes (such as welding, cutting and
      transportation on-site) will add some emissions, but typically less than
      the steelmaking itself. It's not uncommon that more than 80% of a
      fabricated steel product's carbon footprint comes from the steel material.
      Thus, offshore foundation manufacturers cannot rely on default data for
      steel without penalty, because steel's share of emissions will exceed the
      20% threshold.{" "}
    </p>
    <p>
      Only if a precursor is minor (e.g. a small amount of paint or bolts) might
      default data be used without breaching that 20% rule. For completeness,
      CBAM also states that if actual emissions can't be determined or verified,
      the default will be set at the exporting country's average with an extra
      markup. If even that is unavailable, the calculation will be based on the
      worst 10% of exporters' averages. This further underscores that relying on
      default values is a last resort that usually results in a higher reported
      emission intensity.
    </p>
    <h4 className="font-bold">Monitoring methodologies</h4>
    <p>
      Operators have flexibility in how they monitor emissions as long as it
      meets accuracy requirements. The CBAM Implementing Regulation outlines a
      hierarchy of methods and encourages using the best available data.{" "}
    </p>
    <p>
      The two primary approaches are calculation-based monitoring and
      measurement-based monitoring.{" "}
    </p>
    <h5 className="text-lg font-bold">Calculation-based monitoring </h5>
    <p>
      This is the standard method for most. It involves measuring activity data
      (fuel, material consumed) and applying calculation factors (emission
      factors, oxidation rates) to calculate emissions. For example, the factory
      would calculate CO<sub>2</sub> from propane usage by multiplying litres of
      propane burned by a CO<sub>2</sub>-per-litre factor.{" "}
    </p>
    <p>
      The regulation differentiates between combustion emissions (e.g. burning
      fuels for energy) and process emissions (chemical process-related CO
      <sub>2</sub>) in the standard methodology, but the principle is similar:
      quantify inputs and use known factors to compute emissions.{" "}
    </p>
    <p>
      A variant of this is the mass balance methodology, which is mainly
      relevant to complex chemical or metallurgical sites ( such as an
      integrated steel mill where carbon is both in products and emissions, and
      needs balancing). This is probably not needed for a fabrication yard
      (since they don't typically transform materials in a way that carbon is
      stored; they mostly shape and weld steel).{" "}
    </p>
    <p>
      In short: a calculation-based approach using fuel and power consumption
      data is likely the go-to for foundation manufacturers.
    </p>
    <h5 className="text-lg font-bold">Measurement-based monitoring</h5>
    <p>
      In some cases, large installations might directly measure their CO
      <sub>2</sub> emissions from flue gases using Continuous Emissions
      Monitoring Systems (CEMS). This involves sensors and analysers in chimneys
      or exhausts that continuously track the concentration of CO<sub>2</sub>{" "}
      (and other gases) and the flow rate, providing direct emissions data. This
      method can be very accurate but is expensive and generally used only in
      specific industries (for example, it's mandated for certain chemical
      plants like nitric acid production for N<sub>2</sub>O monitoring).{" "}
    </p>
    <p>
      For an offshore foundation plant, full CEMS may be overkill unless they
      have large combustion sources.{" "}
    </p>
    <p>
      No matter which monitoring method is used, the quality of data is
      paramount. The guidance mentions a data quality hierarchy where operators
      should use the most accurate and lowest uncertainty data sources
      available. In practice, that means using calibrated meters, regularly
      maintaining measurement devices, conducting lab tests for fuel properties
      if needed and generally following best practices.{" "}
    </p>
    <p>
      Implementing a data control system - while officially optional - is
      recommended as a best practice. This means having internal checks and
      documentation for all data (e.g. double-checking meter readings, keeping
      records of calculations and conducting internal audits) to ensure
      everything reported can be trusted. It's in the operator's interest to do
      so, because any discrepancies or overestimation could lead to paying for
      extra certificates, while underestimation could lead to compliance issues
      or penalties.
    </p>
    <h4 className="font-bold">
      Here's an example relating to offshore wind fabrication
    </h4>
    <p>
      Consider a monopile manufacturer in a non-EU country that plans to export
      foundations to the EU. The manufacturer defines its fabrication facility
      as including the plate storage area, rolling and welding workshop, and
      coating and painting workshop (everything on their site involved in
      producing the monopile).
    </p>
    <p>Here's how data collection might look. </p>
    <h5 className="text-lg font-bold">Direct emissions</h5>
    <p>
      The fabrication identifies sources like two gas-fired plate rolling
      furnaces (used to heat edges of plates for bending), several diesel
      forklifts and cranes and a back-up diesel generator. They install flow
      meters on the gas lines feeding the furnaces to log daily gas usage.
      Diesel consumption for vehicles is tracked by fuel purchase records and
      hourly usage logs. Each source is assigned an emission factor: e.g. Xkg CO
      <sub>2</sub> per Nm<sup>3</sup> of natural gas (from official data), and
      Ykg CO<sub>2</sub> per litre of diesel. At year's end, they sum up total
      gas and diesel used and calculate CO<sub>2</sub> emitted.
    </p>
    <h5 className="text-lg font-bold">Indirect emissions</h5>
    <p>
      The facility draws electricity from the grid for all welding machines and
      lighting etc. They have electricity meters recording consumption each
      month. Since it's grid power, they plan to use the EU-provided emission
      factor for their country's grid (unless that country has unique
      agreements). Suppose the grid factor is, for instance, 0.7 tonnes CO
      <sub>2</sub> per MWh. Using their total MWh consumed, they calculate the
      indirect CO<sub>2</sub>.{" "}
    </p>
    <h5 className="text-lg font-bold">Precursors</h5>
    <p>
      The monopile manufacturer purchased 50,000 tonnes of steel plate from
      Steel Mill X. They request from Steel Mill X an official statement of the
      mill's specific emissions (direct + indirect) per tonne of steel for the
      year. The mill provides documentation that its emissions are 1.9 tonne of
      CO<sub>2</sub> per tonne of steel (with details on production route being
      blast furnace/basic oxygen furnace, using 20% scrap). They also confirm
      they pay no carbon price domestically (or if they do, they detail
      it).{" "}
    </p>
    <p>
      The manufacturer collects this info, as it will need to pass it to the EU
      importer. Throughout the year, the manufacturer also keeps records
      according to an MMD so that if an auditor or verifier arrives, they can
      show gas meter readings, diesel logs, electricity bills and the steel
      supplier's certificate of emissions. They also implement some internal
      controls - such as cross-checking fuel purchase receipts against meter
      readings monthly and performing a sanity check on the final emissions
      numbers (e.g. comparing the CO<sub>2</sub> per monopile reported this year
      versus last year) to catch anomalies.
    </p>
    <p>
      By following these steps, the non-EU manufacturer can generate a robust
      dataset of embedded emissions per product. In fact, if the steel accounted
      for most emissions, the guidance allows a simplification: if the
      fabrication process contributes no more than 20% of total emissions, the
      manufacturer could even choose to apply a conservative default for their
      own process emissions (like an estimate) as long as the precise steel data
      is obtained.{" "}
    </p>
    <p>
      For instance, if 90% of a monopile's emissions come from the steelmaking
      and only 10% from welding, the yard might use the Commission's default
      factors for plate fabrication emissions (if available) to simplify
      reporting. However, they would still report the actual steel emissions
      from the supplier. This rule exists to ease the burden on small emitters
      in the value chain and focus accuracy where it matters most. In all cases
      though, the non-EU manufacturer must be prepared to have data
      independently verified.
    </p>
    {/* <p>&gt;&gt; CBAM: data verification in the offshore wind sector [Link to other article]</p> */}
    <h3>Data collection for importers (EU customers) </h3>
    <p>
      EU importers of CBAM-covered goods have their own data collection and
      reporting duties. An importer in CBAM terms is typically the party that
      brings the goods into the EU and is responsible for filing the CBAM
      declaration (often called the "authorised CBAM declarant").{" "}
    </p>
    <p>
      For offshore wind components like foundations or towers, this could be a
      developer, an engineering-procurement-construction (EPC) contractor or a
      turbine manufacturer that imports the parts. These importers need to
      gather detailed emissions information from their non-EU suppliers well in
      advance to comply with reporting and, eventually, calculate how many
      certificates to purchase.
    </p>
    <p>
      To facilitate this exchange, the European Commission has provided a
      standardised template (Excel-based) for importers to send to their
      suppliers. This voluntary communication template simplifies the process by
      automatically performing certain calculations once the data is supplied.
      In essence, an EU importer can send this spreadsheet to a steel mill or
      manufacturing plant outside the EU, and the supplier can input the
      required numbers, with the tool computing the emissions per tonne of
      product according to the CBAM methodology.{" "}
    </p>
    <p>
      While using the official template is not mandatory, it's recommended as it
      aligns with what importers must ultimately report.
    </p>
    <h4 className="font-bold">What data are importers expected to collect?</h4>
    <p>
      The CBAM guidance for importers outlines a comprehensive list of
      information they should obtain from each supplier. This information is
      needed for each product type or category being imported. Taking the case
      of importing offshore wind foundations, the importer would request from
      the manufacturer the key data points outlined below.{" "}
    </p>
    <h5 className="text-lg font-bold">
      Basic installation and process information{" "}
    </h5>
    <p>
      The identity of the supplier (name, location) and the description of its
      production processes and routes for the relevant goods. For example, if
      the import is rolled steel plates, the importer would note the mill's
      details and that it used a blast furnace route. If the import is a
      finished monopile, the importer notes the fabrication yard's details and
      any distinct processes such as cutting or welding.{" "}
    </p>
    <h5 className="text-lg font-bold">Embedded emissions of the goods </h5>
    <p>
      The specific direct and indirect emissions per unit of each good being
      imported must be recorded. In practice, this could be expressed as "X
      tonnes CO<sub>2</sub> per tonne of product." If the supplier already did
      the calculations (as described in the previous section), they would
      provide this figure. For instance, a steel mill might report "direct
      emissions: 1.5 tonne CO<sub>2</sub>/tonne steel; indirect emissions: 0.4
      tonne CO<sub>2</sub>/tonne steel; total = 1.9 tonne CO<sub>2</sub>/tonne."
      A monopile manufacturer might report "embedded emissions for a finished
      monopile: Y tonnes CO<sub>2</sub> per tonne of monopile," which would
      include both the steel production and fabrication emissions.
    </p>
    <h5 className="text-lg font-bold">Calculation factors and sources</h5>
    <p>
      The importer must provide details on how the emission factors were
      determined and their sources. The importer should know if the supplier
      used standard emission factors (and from which reference - e.g. IPCC
      guidelines or national data) or custom factors via lab analysis. For
      example, if the steel mill used a default carbon content for coke or a
      specific measured carbon content, that information should be shared.
      Essentially, transparency on whether the emissions were measured directly,
      calculated with default values and so on is required for trustworthiness.
    </p>
    <h5 className="text-lg font-bold">Data quality and methodology </h5>
    <p>
      Information on the monitoring methods used must be submitted - whether the
      emissions were determined by a calculation-based approach or by direct
      measurement, and what kind of data quality controls were in place. Also,
      crucially, whether the emissions values provided are based entirely on
      actual monitoring data or if any estimations/default values were used for
      parts of the calculation.{" "}
    </p>
    <p>
      The importer should know, for instance, if the supplier had to estimate
      the emissions for a minor ingredient using a default because measurement
      wasn't feasible. If default values were used, the supplier should briefly
      explain why actual data couldn't be obtained for that aspect.{" "}
    </p>
    <p>
      In an offshore wind context, imagine a scenario: the foundation
      manufacturer got precise data for steel and its own fuel use, but perhaps
      it used a default for a small component's emissions. The importer's
      template would note the use of a default and the reason (e.g. "coating
      material emissions estimated using default as they represent &lt;1% of
      total").
    </p>
    <h5 className="text-lg font-bold">
      Additional sector-specific parameters{" "}
    </h5>
    <p>
      Some goods require extra information. For steel products, one example
      mentioned is alloy content (from the operator side) because certain alloys
      might influence applicable benchmarks or verifications. For the importer,
      the guidance also references any sector-specific reporting parameters that
      might be needed. In most cases for steel, this could include things like
      the percentage of scrap used or other characteristics that could affect
      emissions (though for finished structures like monopiles, it's primarily
      about the steel and energy inputs).
    </p>
    <h5 className="text-lg font-bold">
      Carbon price and double counting information{" "}
    </h5>
    <p>
      The importer must gather information on any carbon price paid for the
      goods in the country of origin. This is because importers can claim a
      reduction in their CBAM liability equal to any carbon price the producer
      already paid at home (to avoid double taxation of carbon).{" "}
    </p>
    <p>
      For example, if a Turkish steel plate incurs a cost under Turkey's carbon
      scheme, the EU importer can deduct that from the CBAM bill - but only if
      it's properly documented. So the template asks: "Was there a carbon tax or
      ETS cost embedded in this product? If so, how much per tonne, and were
      there any rebates?". For offshore wind components, if a country introduced
      its own carbon fee for steel, that would be noted. The EU wouldn't charge
      again for that portion.
    </p>
    <h5 className="text-lg font-bold">Optional transparency data </h5>
    <p>
      The template also allows any extra information listed in the CBAM guidance
      (see Table 6-3, Part 2 in the Implementing Regulation) for greater
      transparency or cross-checks. This might include things like the specific
      energy mix of the electricity used, the type of fuel used in furnaces and
      so on. While not mandatory, such details can help verifiers or import
      authorities double-check the plausibility of the reported emissions.
    </p>
    <h4 className="font-bold">What it means for EU importers</h4>
    <p>
      The EU importer essentially plays the role of data aggregator and
      reporter. They must compile the above data from each foreign installation
      whose goods they import. Taking a practical offshore wind example: a
      European wind developer importing 10 monopile foundations from outside
      Europe would request all this information from the supplier's factory. If
      those monopiles are composed of steel from two different mills, the
      supplier might also have to provide precursor data for each mill. The
      importer would then consolidate that into their annual declaration.
    </p>
    <p>
      It's important to realise that the accuracy of the CBAM declaration relies
      heavily on the foreign operator's data. Recognising this, CBAM provides a
      carrot-and-stick approach. The "stick" is the default value markup
      mentioned before - if the importer cannot get reliable actual data, they
      resort to defaults which include an extra 10-30% emissions, potentially
      costing more. The "carrot" is that if actual emissions are lower than the
      default intensity, using actual data will save money. For instance,
      suppose the average steel emissions is 2.2 t CO<sub>2</sub>/t (and default
      with markup makes it effectively 2.4 t), but the mill supplying the
      monopile plates has invested in cleaner tech and only emits 1.5 t/t.
      Providing that actual data means the CBAM obligation is based on 1.5, not
      2.4 - a substantial reduction in certificates required. This incentivises
      both the exporter to reduce emissions and document them, and the importer
      to collect all the evidence.
    </p>
    <p>
      To make data collection smoother, many companies are establishing direct
      communication channels between importers and exporters. Large European
      steel importers, for example, have started engaging with their overseas
      suppliers to prepare for CBAM compliance<sup>[6]</sup>
      <sup>[4]</sup>. For the offshore wind sector, this might involve wind
      project developers working closely with foundation and tower manufacturers
      to ensure they understand CBAM requirements. It's a new kind of dialogue
      where emissions transparency becomes part of the supplier contract. We may
      see contract clauses requiring the supplier to provide verified emissions
      data, or even requiring the supplier to undergo certain low-carbon
      certifications to be eligible for contracts.
    </p>
    <p>
      Finally, once the data is collected by the importer, they will use it for
      CBAM declaration and compliance (discussed later in this article). The
      importer must calculate the total embedded emissions based on the reported
      emissions per tonne and the tonnage of goods. They will then determine how
      many CBAM certificates that equates to, adjusting for any carbon prices
      already paid abroad or any free allocation equivalences. All of this
      information flows from the initial data collection - underscoring why
      getting accurate information from the start is so important.
    </p>
    <h3>
      Conclusion: the benefits of accurate data collection for offshore wind
      manufacturers
    </h3>
    <p>
      For manufacturers of offshore wind foundations and other steel components,
      accurate and reliable data collection is not just a compliance exercise -
      it's a strategic advantage in the era of CBAM. By investing in robust
      monitoring of emissions and transparency, these companies can mitigate the
      cost impacts of the new carbon tariff and even turn them to their
      favour.{" "}
    </p>
    <p>Here are the key benefits.</p>
    <h4 className="font-bold">Avoid unnecessary costs</h4>
    <p>
      Precise emissions data allows an importer to pay for the actual carbon
      content of a product, no more and no less. If a manufacturer has lower
      emissions than the industry average, documenting that fully means the CBAM
      charges will be correspondingly lower. It prevents the importer from
      having to fall back on inflated default values with markups. In financial
      terms, this can save millions over large projects. For example, if using
      cleaner Electric Arc Furnace (EAF) steel instead of average Blast Furnace
      (BF) steel cuts the emissions per monopile by 30-50%, the CBAM
      certificates needed (and thus cost) drop similarly - a direct competitive
      edge when bidding for contracts.
    </p>
    <h4 className="font-bold">Market access and competitiveness </h4>
    <p>
      EU offshore wind projects will favour suppliers who make CBAM compliance
      easy and reliable. An overseas foundation manufacturer who can provide a
      fully verified emissions report with their products offers peace of mind
      to EU customers. Those customers face stiff penalties for misreporting, so
      they will gravitate towards business partners with a track record of data
      integrity. In contrast, a supplier who cannot provide credible data might
      effectively be imposing a risk or surcharge (through default values) on
      their product. Data reliability becomes a selling point. We can foresee
      tenders including requirements like "CBAM-ready documentation" or using
      carbon footprint as a criterion when evaluating bids. Manufacturers who
      are ahead in monitoring and verification will likely win more contracts.
    </p>
    {/* <p>&gt;&gt; CBAM: data verification in the offshore wind sector [Link to other article]</p> */}
    <h4 className="font-bold">Carbon reduction and innovation</h4>
    <p>
      The act of measuring often leads to managing. When a company closely
      monitors its energy use and emissions, it tends to identify efficiency
      improvements. In the process of gathering data for CBAM, a foundation
      manufacturer might discover, for instance, that a particular furnace is
      inefficient or that switching to a different welding gas could cut
      emissions. Accurate data collection can thus drive internal optimisation
      and innovation. The benefit is twofold: it lowers operational costs
      (energy saved is money saved) and further lowers the carbon footprint of
      the product. With CBAM in place, such improvements have a quantifiable
      monetary benefit in addition to environmental benefit.
    </p>
    <h4 className="font-bold">Enabling carbon price credits</h4>
    <p>
      Reliable data is needed not only to quantify emissions but also to claim
      any credits for carbon costs already incurred. If an offshore wind
      component manufacturer is in a country that has its own carbon pricing
      (for example, Canada or South Korea with their carbon fees for steel),
      they can ensure that data on those costs are passed to the importer so
      that the CBAM charge is reduced correspondingly. Without proper
      documentation, the importer would have to pay full CBAM duties and the
      upstream carbon cost effectively gets paid twice. So accurate
      record-keeping of any carbon taxes or permits paid can avoid double
      payments and strengthens the case for choosing that supplier.
    </p>
    <h4 className="font-bold">Reduced risk of penalties and delays</h4>
    <p>
      Non-compliance with CBAM - whether through inaccurate reports or missed
      deadlines - can lead to penalties or even blocks on imports. By building a
      robust data collection and verification system, manufacturers help ensure
      their customers (the importers) submit correct declarations on time. This
      avoids disruption in getting goods through customs or unexpected fines
      that could sour business relationships. Good data practices are part of
      good customer service in this new regulatory landscape.
    </p>
    <h4 className="font-bold">Enhanced reputation and market position</h4>
    <p>
      As the world moves toward decarbonisation, being a leader in low-carbon
      manufacturing is a reputational boon. Offshore wind is a flagship of green
      energy and stakeholders (governments, investors, the public) increasingly
      care that the supply chain is green as well. Manufacturers who
      transparently report and reduce emissions can brand themselves as
      sustainable partners in the energy transition. This could open up access
      to green finance or government support and differentiate them from
      competitors. Conversely, if CBAM exposes a producer as having particularly
      high emissions (and no plan to improve), it might face pressure to adapt
      or lose market share.
    </p>
    <h4 className="font-bold">Longterm strategic alignment</h4>
    <p>
      The CBAM is part of a bigger picture of the global economy valuing
      low-carbon goods. Other regions are considering similar mechanisms and
      carbon accounting might become a standard part of trade. By honing data
      collection skills now, offshore wind manufacturers prepare for a future
      where carbon footprints could be as important as price and quality. They
      build internal capacity - training staff, establishing IT systems for
      emissions tracking etc - which will serve them in the long run. In
      essence, they "future-proof" their operations against evolving climate
      regulations worldwide.
    </p>
    <h3>Let us help</h3>
    <p>
      We have deep expertise in CBAM and the global offshore wind supply chain.
      Let us bring confidence to your project.{" "}
      <a href="https://www.windenergybridge.com/about-us">
        Here's what you can expect from us
      </a>
      .{" "}
    </p>
    <h3>You may also be interested in: </h3>
    {/* <p>&gt;&gt; CBAM: data verification in the offshore wind sector [Link to other article]</p> */}
    <p>
      &gt;&gt;{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ wordBreak: "break-all" }}
        href="https://www.windenergybridge.com/insights/cbam:-market-implications-for-the-offshore-wind-sector"
      >
        CBAM: 7 market implications for the offshore wind sector
      </a>
    </p>
    <p>
      &gt;&gt;{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ wordBreak: "break-all" }}
        href="https://www.windenergybridge.com/insights/cbam:-regulations-and-compliance-for-the-offshore-wind-sector"
      >
        CBAM: regulations and compliance for the offshore wind sector
      </a>
    </p>
    <p>
      &gt;&gt;{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ wordBreak: "break-all" }}
        href="https://www.windenergybridge.com/insights/keeping-offshore-wind-developments-on-track-in-a-time-of-expensive-capital"
      >
        Keeping offshore wind developments on track in a time of expensive
        capital
      </a>
    </p>
    <p>
      &gt;&gt;{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ wordBreak: "break-all" }}
        href="https://www.windenergybridge.com/insights/6-reasons-why-uk-offshore-wind-requires-a-'glocal'-approach"
      >
        6 reasons why UK offshore wind requires a 'glocal' approach
      </a>
    </p>
    <h3>References</h3>
    <ul className="list-disc list-inside">
      <li>
        [1][2][3][7] Canary Media: Europe's world-first carbon tariff is coming.
        Here's what to know.{" "}
        <a
          href="https://www.canarymedia.com/articles/clean-industry/europe-cbam-carbon-tariff-imports"
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          https://www.canarymedia.com/articles/clean-industry/europe-cbam-carbon-tariff-imports
        </a>
      </li>
      <li>
        [4][5][6] The Carbon Trust: CBAM: What it means for exporters of steel,
        iron and aluminium.{" "}
        <a
          href="https://www.carbontrust.com/news-and-insights/insights/cbam-what-it-means-for-exporters-of-steel-iron-and-aluminium"
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          https://www.carbontrust.com/news-and-insights/insights/cbam-what-it-means-for-exporters-of-steel-iron-and-aluminium
        </a>
      </li>
      <li>
        [8] Swift File: CBAM List of Goods Covered.{" "}
        <a
          href="https://swiftfile.ie/wp-content/uploads/2024/06/SwiftFile-CBAM-goods-list.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          https://swiftfile.ie/wp-content/uploads/2024/06/SwiftFile-CBAM-goods-list.pdf
        </a>
      </li>
      <li>
        [9] CRU Group: Offshore wind and steel: a huge mutual dependency.{" "}
        <a
          href="https://www.crugroup.com/en/communities/thought-leadership/2021/offshore-wind-and-steel-a-huge-mutual-dependency/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          https://www.crugroup.com/en/communities/thought-leadership/2021/offshore-wind-and-steel-a-huge-mutual-dependency/
        </a>
      </li>
      <li>
        [10] WindEurope: Wind energy in Europe: 2024 Statistics and the outlook
        for 2025-2030{" "}
        <a
          href="https://windeurope.org/dat/aproducts/wind-energy-in-europe-2024-statistics-and-the-outlook-for-2025-2030/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          https://windeurope.org/dat/aproducts/wind-energy-in-europe-2024-statistics-and-the-outlook-for-2025-2030/
        </a>
      </li>
      <li>
        [11] ArcelorMittal: Steel Thoughts - Underpinning Europe's renewable
        energy revolution{" "}
        <a
          href="https://corporate.arcelormittal.com/smarter-future/steel-thoughts-underpinning-europe-s-renewable-energy-revolution"
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          https://corporate.arcelormittal.com/smarter-future/steel-thoughts-underpinning-europe-s-renewable-energy-revolution
        </a>
      </li>
      <li>
        [12] Esgian: Chinese suppliers gain a foothold in Europe's offshore wind
        market.{" "}
        <a
          href="https://esgian.com/2022/10/11/chinese-suppliers-gain-a-foothold-in-europes-offshore-wind-market/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          https://esgian.com/2022/10/11/chinese-suppliers-gain-a-foothold-in-europes-offshore-wind-market/
        </a>
      </li>
      <li>
        [13] Alicja Kucewicz, Ramboll, Haw Hamburg: Life Cycle Assessment of
        Selected Offshore Wind Foundations: Comparing Primary Design Structures
        from Development to Installation Phase.{" "}
        <a
          href="https://reposit.haw-hamburg.de/bitstream/20.500.12738/16395/1/Life_Cycle_Assessment_of_selected_Offshore_Wind_Foundations.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          https://reposit.haw-hamburg.de/bitstream/20.500.12738/16395/1/Life_Cycle_Assessment_of_selected_Offshore_Wind_Foundations.pdf
        </a>
      </li>
    </ul>
    <br />

    <br />
    <br />
    <br />
    <p>
      As the global offshore wind sector continues to advance, innovation,
      investments, and supportive policies are driving transformative growth. At{" "}
      <i>
        <b>WeBridge</b>
      </i>
      , we provide the expertise and insights our partners need to confidently
      navigate this evolving landscape.
    </p>
    <h3 style={{ marginTop: "200px" }}>Subscribe</h3>
    <ContactSubscribe />
  </div>,
);
